import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowMonitoringApiService } from '../services/workflow-monitoring-api.service';
import { WorkflowApiService } from '../services/workflow-api.service';
import {
  WorkflowMonitoringDashboardResponse,
  WorkflowActiveEnrollmentsResponse,
  WorkflowActiveEnrollmentItem
} from '../models/workflow-monitoring.model';
import { WorkflowResponse } from '../models/workflow.model';
import { WorkflowTimelineModalComponent } from '../workflow-timeline-modal/workflow-timeline-modal.component';
import { WorkflowMonitoringSummaryComponent } from '../workflow-monitoring-summary/workflow-monitoring-summary.component';
import { WorkflowNodeLabelService } from '../shared/workflow-node-label.service';
import {
  workflowEnrollmentSourceLabel,
  workflowObjectTypeLabel,
  workflowQueueTypeLabel,
  workflowStatusLabel
} from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-monitoring',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule,
    WorkflowTimelineModalComponent, WorkflowMonitoringSummaryComponent
  ],
  templateUrl: './workflow-monitoring.component.html'
})
export class WorkflowMonitoringComponent implements OnInit, OnDestroy {
  workflowId!: number;
  workflow: WorkflowResponse | null = null;

  // ── Dashboard ─────────────────────────────────────────────────────────────
  rangeDays: 30 | 60 | 90 = 30;
  includeTestRuns = false;
  dashboard: WorkflowMonitoringDashboardResponse | null = null;
  dashboardLoading = false;
  dashboardError = '';

  // ── Active enrollments ────────────────────────────────────────────────────
  activeData: WorkflowActiveEnrollmentsResponse | null = null;
  activeLoading = false;
  activeError = '';

  // ── Timeline ──────────────────────────────────────────────────────────────
  timelineEnrollmentId: number | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private monitoringApi: WorkflowMonitoringApiService,
    private workflowApi: WorkflowApiService,
    private nodeLabels: WorkflowNodeLabelService
  ) {}

  /**
   * Etiquetas legibles del diccionario único del módulo. `WorkflowActiveEnrollmentItem` viaja sin
   * campos `*Label` (a diferencia del DTO de timeline), así que la tabla pintaba `CONTACT`,
   * `WAITING` y el id crudo del nodo.
   */
  readonly statusText = workflowStatusLabel;
  readonly objectTypeText = workflowObjectTypeLabel;
  readonly queueTypeText = workflowQueueTypeLabel;
  readonly enrollmentSourceText = workflowEnrollmentSourceLabel;

  nodeText(nodeId: number | null | undefined): string {
    return this.nodeLabels.labelFor(this.workflowId, nodeId);
  }

  ngOnInit(): void {
    this.workflowId = Number(this.route.snapshot.paramMap.get('id'));
    this.workflowApi.getById(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (wf) => { this.workflow = wf; },
      error: () => {}
    });
    this.loadDashboard();
    this.loadActiveEnrollments();
    this.nodeLabels.load(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────

  loadDashboard(): void {
    this.dashboardLoading = true;
    this.dashboardError = '';
    this.monitoringApi.getMonitoringDashboard(this.workflowId, {
      rangeDays: this.rangeDays,
      includeTestRuns: this.includeTestRuns
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.dashboard = res; this.dashboardLoading = false; },
      error: (err) => {
        this.dashboardError = err.error?.message ?? 'No se pudo cargar el dashboard.';
        this.dashboardLoading = false;
      }
    });
  }

  // ── Active enrollments ────────────────────────────────────────────────────

  loadActiveEnrollments(): void {
    this.activeLoading = true;
    this.activeError = '';
    this.monitoringApi.getActiveEnrollments(this.workflowId, {
      includeTestRuns: this.includeTestRuns
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.activeData = res; this.activeLoading = false; },
      error: (err) => {
        this.activeError = err.error?.message ?? 'No se pudieron cargar los registros activos.';
        this.activeLoading = false;
      }
    });
  }

  reload(): void {
    this.loadDashboard();
    this.loadActiveEnrollments();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  formatDuration(ms: number | null | undefined): string {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  }

  formatTimeUntil(ms: number | null | undefined): string {
    if (!ms || ms <= 0) return 'Inminente';
    if (ms < 60000)   return `${Math.floor(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  }

  getStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':    return 'bg-green-100 text-green-700';
      case 'WAITING':   return 'bg-yellow-100 text-yellow-700';
      case 'COMPLETED': return 'bg-blue-100 text-blue-700';
      case 'FAILED':    return 'bg-red-100 text-red-700';
      case 'PAUSED':    return 'bg-orange-100 text-orange-700';
      case 'CANCELLED': return 'bg-gray-200 text-gray-600';
      case 'EXITED':    return 'bg-gray-100 text-gray-500';
      default:          return 'bg-gray-100 text-gray-500';
    }
  }

  isTestRunItem(item: WorkflowActiveEnrollmentItem): boolean {
    return item.enrollmentSource === 'TEST_RUN';
  }

  get timeSeriesColumns(): string[] {
    if (!this.dashboard?.timeSeries?.length) return [];
    const keys = Object.keys(this.dashboard.timeSeries[0]).filter(k => k !== 'date');
    return keys;
  }

  /** Las columnas salen del DTO en inglés; la tabla es de cara al usuario. */
  timeSeriesLabel(col: string): string {
    const labels: Record<string, string> = {
      enrolled:  'Inscritos',
      completed: 'Exitosos',
      failed:    'Fallidos',
      exited:    'Salidos',
      cancelled: 'Cancelados'
    };
    return labels[col] ?? col;
  }
}
